/**
 * features/finance/ExamFeesScreen.tsx
 * ------------------------------------------------------------------
 * Bulk invoice creation for exam (or other near-uniform) fees, driven
 * by a template the app generates:
 *
 *   1) Pick a cohort (a form level, or everyone), a default amount, a
 *      batch reference, term/year and due date.
 *   2) Download a CSV template pre-filled with that cohort's admission
 *      numbers, names and the default amount.
 *   3) The exams department edits only the amounts that differ (and may
 *      blank an amount to skip a student), then you upload it back.
 *   4) Preview matched/unmatched/skipped + totals, then create.
 *
 * Idempotent: every invoice id is exam_<batch>_<studentId>, so
 * re-uploading the same batch UPDATES rather than duplicating. Exam fees
 * carry no discounts (sibling/prepay are tuition-only), so each bills
 * exactly the amount entered. No new dependencies — CSV is parsed and
 * built inline.
 * ---------------------------------------------------------------- */
import { useMemo, useRef, useState } from 'react';
import {
  FileSpreadsheet, Download, Upload, CheckCircle2, AlertTriangle, ArrowLeft, Mail,
} from 'lucide-react';
import { AccentButton, GradientHero, SoftCard, glassFieldClass } from '@/features/finance/ui';

import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { finance } from '@/features/finance/financeWrite';
import { recordFinanceAudit } from '@/lib/financeAudit';
import { getPrimaryContact } from '@/lib/admissions';
import { deliverInvoice } from '@/lib/invoiceDelivery';
import { SCHOOL, termLabel, currentTermKey, type TermKey } from '@/lib/school.config';
import { assembleInvoice, fmtPula, formatInvoiceNo, FEE_CATEGORY_LABELS, type FeeCategory } from '@/lib/financeCore';

/* ----------------------------- CSV utils ----------------------------- */
function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'batch';
}
function csvCell(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}
function buildCsv(rows: string[][]): string {
  return rows.map((r) => r.map((c) => csvCell(c ?? '')).join(',')).join('\r\n');
}
/** Minimal RFC-4180-ish parser: quoted fields, embedded commas/quotes, CRLF. */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  const s = text.replace(/^\uFEFF/, ''); // strip BOM
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
      } else { field += c; }
    } else if (c === '"') { inQuotes = true; }
    else if (c === ',') { row.push(field); field = ''; }
    else if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
    else if (c === '\r') { /* ignore, handled by \n */ }
    else { field += c; }
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

interface PreviewRow {
  admissionNo: string;
  name: string;
  studentId?: string;
  amount: number;
  description: string;
  matched: boolean;
  isUpdate: boolean;
}

export default function ExamFeesScreen() {
  const { userData } = useAuth();
  const { students, classById } = useData();
  const fin = useFinanceData();

  const actor = { id: userData?.uid ?? 'unknown', email: userData?.email };
  const year = SCHOOL.selectableYears[0] ?? String(new Date().getFullYear());

  const [batchRef, setBatchRef] = useState('');
  const [category, setCategory] = useState<FeeCategory>('other');
  const [term, setTerm] = useState<TermKey>(currentTermKey());
  const [cohortForm, setCohortForm] = useState('all');
  const [defaultAmount, setDefaultAmount] = useState('');
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  });
  const [emailOnCreate, setEmailOnCreate] = useState(false);
  const [phase, setPhase] = useState<'setup' | 'review'>('setup');
  const [rows, setRows] = useState<PreviewRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [sc, setSc] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>(
    { show: false, status: 'saving', message: '' },
  );
  const fileRef = useRef<HTMLInputElement>(null);

  const formOf = (s: { currentClass: string }) => classById(s.currentClass)?.form ?? '';
  const forms = useMemo(() => {
    const set = new Set<string>();
    students.forEach((s) => { const f = formOf(s); if (f) set.add(f); });
    return Array.from(set).sort();
  }, [students]); // eslint-disable-line react-hooks/exhaustive-deps

  const studentsById = useMemo(() => {
    const m: Record<string, (typeof students)[number]> = {};
    students.forEach((s) => { m[s.id] = s; });
    return m;
  }, [students]);
  const studentByAdm = useMemo(() => {
    const m: Record<string, (typeof students)[number]> = {};
    students.forEach((s) => { if (s.admissionNumber) m[s.admissionNumber.trim().toUpperCase()] = s; });
    return m;
  }, [students]);

  const cohort = useMemo(
    () => students.filter((s) => s.admissionNumber && (cohortForm === 'all' || formOf(s) === cohortForm)),
    [students, cohortForm], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const idFor = (studentId: string) => `bulk_${slugify(batchRef)}_${studentId}`;

  function downloadTemplate() {
    if (!batchRef.trim()) { say('Enter a batch reference first.', 'error'); return; }
    if (cohort.length === 0) { say('No students with admission numbers in that cohort.', 'error'); return; }
    const header = ['admissionNo', 'studentName', 'amount', 'description'];
    const body = cohort.map((s) => [
      s.admissionNumber as string,
      s.fullName,
      defaultAmount.trim(),
      batchRef.trim(),
    ]);
    const csv = buildCsv([header, ...body]);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slugify(batchRef)}-template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function onUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    if (fileRef.current) fileRef.current.value = '';
  }

  function handleFile(file: File) {
    if (!batchRef.trim()) { say('Enter the same batch reference before uploading.', 'error'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const grid = parseCsv(String(reader.result ?? ''));
        if (grid.length < 2) { say('That CSV has no data rows.', 'error'); return; }
        const header = grid[0].map((h) => h.trim().toLowerCase());
        const col = (name: string) => header.indexOf(name);
        const iAdm = col('admissionno');
        const iAmt = col('amount');
        const iDesc = col('description');
        const iName = col('studentname');
        if (iAdm < 0 || iAmt < 0) { say('CSV needs at least admissionNo and amount columns.', 'error'); return; }

        const existingIds = new Set(fin.invoices.map((i) => i.id));
        const out: PreviewRow[] = [];
        for (const r of grid.slice(1)) {
          const admissionNo = (r[iAdm] ?? '').trim();
          if (!admissionNo) continue;
          const amount = Math.round(Number((r[iAmt] ?? '').replace(/[^0-9.\-]/g, '')) * 100) / 100;
          const student = studentByAdm[admissionNo.toUpperCase()];
          out.push({
            admissionNo,
            name: student?.fullName ?? (iName >= 0 ? (r[iName] ?? '').trim() : '') ?? '',
            studentId: student?.id,
            amount: Number.isFinite(amount) ? amount : 0,
            description: (iDesc >= 0 ? (r[iDesc] ?? '').trim() : '') || batchRef.trim(),
            matched: !!student,
            isUpdate: !!student && existingIds.has(idFor(student.id)),
          });
        }
        setRows(out);
        setPhase('review');
        setSc((s) => ({ ...s, show: false }));
      } catch (err) {
        say(`Could not read that file: ${(err as Error).message}`, 'error');
      }
    };
    reader.onerror = () => say('Could not read that file.', 'error');
    reader.readAsText(file);
  }

  function say(message: string, status: 'saving' | 'success' | 'error' = 'saving') {
    setSc({ show: true, status, message });
  }
  function editAmount(idx: number, value: string) {
    const amount = Math.round(Number(value.replace(/[^0-9.\-]/g, '')) * 100) / 100;
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, amount: Number.isFinite(amount) ? amount : 0 } : r)));
  }

  const billable = useMemo(() => rows.filter((r) => r.matched && r.amount > 0), [rows]);
  const unmatched = useMemo(() => rows.filter((r) => !r.matched), [rows]);
  const skipped = useMemo(() => rows.filter((r) => r.matched && r.amount <= 0), [rows]);
  const grandTotal = useMemo(() => billable.reduce((a, r) => a + r.amount, 0), [billable]);
  const updates = billable.filter((r) => r.isUpdate).length;

  async function create() {
    if (busy || billable.length === 0) return;
    setBusy(true);
    say(`Creating ${billable.length} invoices…`, 'saving');
    try {
      const issuedAt = new Date().toISOString();
      const dueIso = new Date(`${dueDate}T12:00:00`).toISOString();
      let seq = fin.invoices.filter((i) => i.year === year).length + 1;

      const invoices = billable.map((r) => {
        const sid = r.studentId as string;
        const id = idFor(sid);
        const existing = fin.invoices.find((i) => i.id === id);
        const student = studentsById[sid];
        return assembleInvoice({
          id,
          invoiceNo: existing?.invoiceNo ?? formatInvoiceNo(fin.settings, { year, term, seq: seq++ }),
          studentId: sid,
          studentName: r.name,
          familyId: student?.familyId,
          term,
          year,
          issuedAt,
          dueDate: dueIso,
          lines: [{ label: r.description, category, amount: r.amount }],
          rules: [], // exam fees: no discounts
        });
      });

      for (let i = 0; i < invoices.length; i += 50) {
        await Promise.all(invoices.slice(i, i + 50).map((d) => finance.invoices.set(d.id, d as unknown as Record<string, unknown>)));
      }
      await recordFinanceAudit({
        action: 'invoice.bulk',
        actor,
        subjectId: slugify(batchRef),
        after: { created: invoices.length, total: grandTotal, term, year },
      });

      let emailed = 0;
      if (emailOnCreate) {
        say(`Invoices created — emailing ${invoices.length}…`, 'saving');
        for (let i = 0; i < invoices.length; i += 10) {
          await Promise.all(
            invoices.slice(i, i + 10).map(async (inv) => {
              const student = studentsById[inv.studentId];
              const toEmail = student ? getPrimaryContact(student)?.email : undefined;
              if (!toEmail) return;
              const res = await deliverInvoice(inv, { toEmail, studentName: inv.studentName ?? '' });
              if (res.emailed) emailed += 1;
            }),
          );
        }
      }

      say(
        `Created ${invoices.length} invoice(s) totalling ${fmtPula(grandTotal)}`
        + `${updates ? ` (${updates} updated)` : ''}`
        + `${emailOnCreate ? ` · emailed ${emailed}` : ''}.`,
        'success',
      );
      setRows([]);
      setPhase('setup');
    } catch (e) {
      console.error(e);
      say(`Creation failed: ${(e as Error).message}. Safe to retry — existing invoices are updated, not duplicated.`, 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <GradientHero icon={FileSpreadsheet} title="Bulk invoices" subtitle="Invoice a cohort from a CSV — exam fees, library fines, trips, or any one-off charge" />
      <SaveConfirmation show={sc.show} status={sc.status} message={sc.message} onDismiss={() => setSc((s) => ({ ...s, show: false }))} />

      {phase === 'setup' ? (
        <SoftCard hover={false}>
          <div className="space-y-4">
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Batch reference</span>
              <input
                value={batchRef}
                onChange={(e) => setBatchRef(e.target.value)}
                placeholder="e.g. Term 2 Exam Fees, Library Fines, Grade 7 Trip"
                className={glassFieldClass('py-2 w-full')}
              />
              <span className="text-[11px] text-gray-400 mt-1 block">
                Identifies this batch. Re-uploading the same reference updates those invoices instead of duplicating.
              </span>
            </label>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <label className="block">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Charge type</span>
                <select value={category} onChange={(e) => setCategory(e.target.value as FeeCategory)} className={glassFieldClass('py-2 w-full')}>
                  {(Object.keys(FEE_CATEGORY_LABELS) as FeeCategory[]).map((c) => (
                    <option key={c} value={c}>{FEE_CATEGORY_LABELS[c]}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Cohort</span>
                <select value={cohortForm} onChange={(e) => setCohortForm(e.target.value)} className={glassFieldClass('py-2 w-full')}>
                  <option value="all">All students</option>
                  {forms.map((f) => <option key={f} value={f}>Form {f}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Term</span>
                <select value={term} onChange={(e) => setTerm(e.target.value as TermKey)} className={glassFieldClass('py-2 w-full')}>
                  {Object.keys(SCHOOL.terms).map((t) => <option key={t} value={t}>{termLabel(t as TermKey)}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Default amount</span>
                <input inputMode="decimal" value={defaultAmount} onChange={(e) => setDefaultAmount(e.target.value)} placeholder="0.00" className={glassFieldClass('py-2 w-full')} />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Due date</span>
                <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={glassFieldClass('py-2 w-full')} />
              </label>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <AccentButton icon={Download} onClick={downloadTemplate}>
                Download template ({cohort.length})
              </AccentButton>
              <span className="text-gray-300 dark:text-gray-600">→ fill amounts →</span>
              <button
                onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                <Upload size={15} /> Upload filled CSV
              </button>
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onUpload} className="hidden" />
            </div>
          </div>
        </SoftCard>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="To invoice" value={String(billable.length)} tone="text-gray-900 dark:text-white" />
            <Stat label="Total" value={fmtPula(grandTotal)} tone="text-gray-900 dark:text-white" />
            <Stat label="Updates" value={String(updates)} tone="text-blue-600 dark:text-blue-300" />
            <Stat label="Unmatched" value={String(unmatched.length)} tone={unmatched.length ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'} />
          </div>

          {unmatched.length > 0 && (
            <SoftCard hover={false}>
              <p className="flex items-start gap-2 text-sm text-red-700 dark:text-red-300">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                {unmatched.length} admission number(s) didn't match a student and will be skipped: {unmatched.map((r) => r.admissionNo).join(', ')}.
              </p>
            </SoftCard>
          )}
          {skipped.length > 0 && (
            <SoftCard hover={false}>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {skipped.length} matched student(s) have a blank/zero amount — left out of this run.
              </p>
            </SoftCard>
          )}

          <SoftCard hover={false} pad="p-0">
            <div className="max-h-[420px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white dark:bg-gray-800">
                  <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="py-2 px-4 font-medium">Student</th>
                    <th className="py-2 px-3 font-medium">Adm. no.</th>
                    <th className="py-2 px-3 font-medium text-right">Amount</th>
                    <th className="py-2 px-4 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {rows.map((r, idx) => (
                    <tr key={`${r.admissionNo}-${idx}`} className={!r.matched ? 'opacity-50' : ''}>
                      <td className="py-1.5 px-4 text-gray-800 dark:text-gray-100">{r.name || '—'}</td>
                      <td className="py-1.5 px-3 text-gray-500 dark:text-gray-400">{r.admissionNo}</td>
                      <td className="py-1.5 px-3 text-right">
                        <input
                          inputMode="decimal"
                          value={r.amount || ''}
                          onChange={(e) => editAmount(idx, e.target.value)}
                          disabled={!r.matched}
                          className={glassFieldClass('py-1 w-28 text-right')}
                        />
                      </td>
                      <td className="py-1.5 px-4">
                        {!r.matched
                          ? <span className="text-xs text-red-600 dark:text-red-400">no match</span>
                          : r.amount <= 0
                            ? <span className="text-xs text-gray-400">skipped</span>
                            : r.isUpdate
                              ? <span className="text-xs text-blue-600 dark:text-blue-300">update</span>
                              : <span className="text-xs text-emerald-600 dark:text-emerald-400">new</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SoftCard>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              onClick={() => { setPhase('setup'); setRows([]); }}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              <ArrowLeft size={15} /> Back
            </button>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer">
                <input type="checkbox" checked={emailOnCreate} onChange={(e) => setEmailOnCreate(e.target.checked)} />
                <Mail size={14} /> Email parents
              </label>
              <AccentButton icon={CheckCircle2} onClick={create} disabled={busy || billable.length === 0}>
                {busy ? 'Working…' : `Create ${billable.length} invoice${billable.length === 1 ? '' : 's'}`}
              </AccentButton>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2">
      <p className="text-[11px] uppercase tracking-wide text-gray-400">{label}</p>
      <p className={`text-base font-bold tabular-nums ${tone}`}>{value}</p>
    </div>
  );
}

export { ExamFeesScreen };
